import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import apiClient from "../lib/api";

const AuthContext = createContext(null);

// ── Local storage helpers ─────────────────────────────────────────────────────
const TOKEN_KEY = "pv_token";
const USER_KEY  = "pv_user";

const saveSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const getCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); }
  catch { return null; }
};

/**
 * Decode a JWT locally (without signature check) to get basic payload.
 * Used as a last-resort fallback when the network is unavailable.
 */
const decodeJwtLocally = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) return null; // truly expired
    return {
      id:    payload.sub,
      email: payload.email,
      role:  payload.role,
      plan:  payload.role === "admin" ? "vip" : "free",
      name:  payload.name || "",
    };
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false); // prevent concurrent refresh calls

  const refresh = useCallback(async () => {
    // Prevent duplicate concurrent refresh calls
    if (isRefreshing.current) return;
    isRefreshing.current = true;

    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      setUser(null);
      setLoading(false);
      isRefreshing.current = false;
      return;
    }

    // ── Step 1: Restore from cache immediately (no loading flash) ────────────
    const cached = getCachedUser();
    if (cached) setUser(cached);

    // ── Step 2: Verify with server ───────────────────────────────────────────
    try {
      const res = await apiClient.get("/auth/me");
      let serverUser = res.data;

      // ROLE GUARD: JWT is cryptographically signed and authoritative for role.
      // If JWT says "admin" but server returns something else (e.g. due to a
      // transient DB issue or stale cache), keep the JWT role.
      // ALSO: if the cached user says "admin" (last known good state), protect
      // the role even when the JWT is near-expiry or has a clock-skew issue.
      // This prevents role downgrades mid-session.
      const jwtData = decodeJwtLocally(token);
      const isAdminByJwt = jwtData?.role === "admin";
      const isAdminByCache = cached?.role === "admin";
      if ((isAdminByJwt || isAdminByCache) && serverUser.role !== "admin") {
        console.warn(
          "[AuthContext] Role downgrade blocked — jwt=" + (jwtData?.role || "null") +
          ", cache=" + (cached?.role || "null") +
          ", server=" + serverUser.role
        );
        serverUser = { ...serverUser, role: "admin", plan: "vip" };
      }

      setUser(serverUser);
      localStorage.setItem(USER_KEY, JSON.stringify(serverUser));
    } catch (err) {
      const status = err?.response?.status;

      if (status === 401 || status === 403) {
        // Token is truly invalid or expired → full logout
        clearSession();
        setUser(null);
      } else {
        // Network error / timeout / 500 → keep session alive
        // Use cached user if available, else decode JWT locally
        const fallback = cached || decodeJwtLocally(token);
        if (fallback) {
          setUser(fallback);
          // Re-persist in case cache was empty
          localStorage.setItem(USER_KEY, JSON.stringify(fallback));
        } else {
          // Can't recover anything → logout
          clearSession();
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
      isRefreshing.current = false;
    }
  }, []);

  // Initial session check on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-validate session when app comes back to foreground (mobile/PWA)
  // Handles the case where the browser discards the tab and the user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          // Only re-validate if we have a token — silent background check
          refresh();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refresh]);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    saveSession(res.data.token, res.data.user);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithData = (token, userData) => {
    saveSession(token, userData);
    setUser(userData);
  };

  const register = async (email, name, password) => {
    const res = await apiClient.post("/auth/register", { email, name, password });
    const token = res.data?.token;
    const user  = res.data?.user;
    if (!token || !user) throw new Error("Respuesta del servidor inválida");
    saveSession(token, user);
    setUser(user);
    return user;
  };

  const logout = () => {
    clearSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithData, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
